import { formatPercent } from '#number/format-percent';

import type { CompressionResult } from './types/compression-result.type';
import type { CompressionOptions } from './types/compression.options';

import { CHUNK_SIZE } from './constants/chunk-size';

/**
 * Compresses a given File using gzip compression and returns a CompressionResult.
 *
 * The function reads the input file in fixed-size chunks, compresses the data using the
 * CompressionStream API, and tracks progress via an optional callback. The compressed
 * data is merged into a single Uint8Array and returned as a new File object.
 *
 * If an `AbortSignal` is supplied and triggered, the chunk loop stops at the next iteration
 * boundary, the writer is aborted to release the stream, and a `DOMException` with name
 * `'AbortError'` is thrown. The caller should catch this specifically to distinguish
 * cancellation from an actual compression failure.
 *
 * @param file - The File object to be compressed.
 * @param options - Optional compression settings, including:
 *   - onProgress: Callback function to report progress as a number between 0 and 1.
 *   - outputFilename: Optional name for the resulting compressed file.
 *   - signal: Optional AbortSignal to support cancellation.
 * @returns A Promise that resolves to a CompressionResult containing the compressed file,
 *          original and compressed sizes, and reduction percentage.
 *
 * @throws {DOMException} When cancelled via the supplied AbortSignal (name: 'AbortError').
 * @throws Will throw if the compression stream fails or file reading encounters an error.
 */
export const compressFile = async (file: File, options: CompressionOptions = {}): Promise<CompressionResult> => {
  const { onProgress, outputFilename, signal } = options;
  const totalBytes = file.size;

  const compressionStream = new CompressionStream('gzip');
  const writer = compressionStream.writable.getWriter();

  // Drain the readable side concurrently while we write
  const chunks: Uint8Array[] = [];
  const reader = compressionStream.readable.getReader();
  const readPromise = (async () => {
    for (;;) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }
      chunks.push(value);
    }
  })();

  // Feed the file in fixed-size chunks, checking the signal between each write
  let offset = 0;

  try {
    while (offset < totalBytes) {
      if (signal?.aborted === true) {
        throw new DOMException('Compression aborted', 'AbortError');
      }

      const slice = file.slice(offset, offset + CHUNK_SIZE);
      const buffer = await slice.arrayBuffer();

      await writer.write(new Uint8Array(buffer));
      offset += buffer.byteLength;
      onProgress?.(Math.min(offset / totalBytes, 1));
    }

    await writer.close();
  } catch (err) {
    // Abort the writer so the reader side drains and readPromise resolves cleanly,
    // then re-throw so the caller can handle cancellation vs real errors.
    await writer.abort(err);
    await readPromise;

    throw err;
  }

  await readPromise;

  // Merge compressed chunks into a single Uint8Array
  const totalLength = chunks.reduce((sum, chunk) => {
    return sum + chunk.length;
  }, 0);
  const merged = new Uint8Array(totalLength);
  let pos = 0;

  for (const chunk of chunks) {
    merged.set(chunk, pos);
    pos += chunk.length;
  }

  const name = outputFilename ?? `${file.name}.gz`;
  const compressedFile = new File([merged], name, { type: 'application/gzip' });

  return {
    compressedSize: compressedFile.size,
    file: compressedFile,
    originalSize: file.size,
    reductionPct: formatPercent(Number.parseFloat((((file.size - compressedFile.size) / file.size) * 100).toString())),
  };
};
